export type KiVerbindungZustand = 'FIRMA_AUS' | 'GESPERRT' | 'AN' | 'AUS';

export type KiAnbieterTyp = 'OPENAI' | 'AZURE_OPENAI' | 'MISTRAL' | 'OLLAMA' | 'EIGENER_ENDPUNKT';

export type KiRegion = 'EU' | 'DRITTLAND' | 'LOKAL';

export interface KiProjektFunktionen {
  testdesign: boolean;
  pipelineAnalyse: boolean;
}

/** Antwort von `GET /api/v1/projects/{projectId}/ki-verbindung`; das Token selbst kommt nie zurück. */
export interface KiVerbindung {
  zustand: KiVerbindungZustand;
  aktiv: boolean;
  anbieter?: KiAnbieterTyp | null;
  modell?: string | null;
  endpunkt?: string | null;
  region?: KiRegion | null;
  tokenGespeichertAm?: string | null;
  kontextfenster: number;
  toolCalling: boolean;
  funktionen: KiProjektFunktionen;
  aufbewahrungTage: number;
  verbrauchTokensMonat: number;
  kontingentTokensProMonat: number | null;
  /** Erfolgreicher Verbindungstest seit der letzten Änderung an Anbieter, Modell, Endpunkt oder Token. */
  verbindungGueltig?: boolean;
  verbindungGetestetAm?: string | null;
  erlaubteAnbieter: KiAnbieterTyp[];
  /** Regionen, die der Mandant zulässt; fehlt das Feld, prüft die Karte die Region nicht vorab. */
  erlaubteRegionen?: KiRegion[];
  erlaubteFunktionen: KiProjektFunktionen;
  maxAufbewahrungTage: number;
}

/** Body von `PUT …/ki-verbindung`; `apiToken` nur, wenn der Nutzer ein neues Token eingegeben hat. */
export interface KiVerbindungSpeichern {
  aktiv: boolean;
  anbieter: KiAnbieterTyp | null;
  modell: string | null;
  endpunkt: string | null;
  apiToken?: string;
  kontextfenster: number;
  toolCalling: boolean;
  funktionen: KiProjektFunktionen;
  aufbewahrungTage: number;
}

/** Antwort von `POST …/ki-verbindung/test`; der Body ist derselbe wie beim Speichern. */
export interface KiVerbindungTestErgebnis {
  ok: boolean;
  dauerMs?: number | null;
  fehlercode?: string | null;
  fehlertext?: string | null;
}

export interface KiAnbieterMeta {
  typ: KiAnbieterTyp;
  /** Leer bei Ollama und dem eigenen Endpunkt: deren Region folgt aus der Adresse. */
  region: KiRegion | null;
  /** Gesetzt bei Anbietern mit festem Endpunkt; das Feld ist dann nur lesbar. */
  festerEndpunkt: string | null;
  tokenOptional: boolean;
}

export const KI_ANBIETER: KiAnbieterMeta[] = [
  { typ: 'OPENAI', region: 'DRITTLAND', festerEndpunkt: 'https://api.openai.com/v1', tokenOptional: false },
  { typ: 'AZURE_OPENAI', region: 'EU', festerEndpunkt: null, tokenOptional: false },
  { typ: 'MISTRAL', region: 'EU', festerEndpunkt: 'https://api.mistral.ai/v1', tokenOptional: false },
  { typ: 'OLLAMA', region: null, festerEndpunkt: null, tokenOptional: true },
  { typ: 'EIGENER_ENDPUNKT', region: null, festerEndpunkt: null, tokenOptional: false },
];

export const KI_AUFBEWAHRUNG_OPTIONEN = [7, 14, 30, 60, 90];

export const KI_MODELL_MAX = 100; // Backend-@Size
export const KI_TOKEN_MAX = 4096; // Backend-@Size
export const KI_KONTEXTFENSTER_MAX = 10_000_000; // Backend-@Max

const OKTETT = '(0|[1-9]\\d{0,2})';
const IPV4 = new RegExp(`^${OKTETT}\\.${OKTETT}\\.${OKTETT}\\.${OKTETT}$`);

export function hostVon(adresse: string | null | undefined): string | null {
  const getrimmt = (adresse ?? '').trim();
  if (!getrimmt) return null;
  try {
    const host = new URL(getrimmt.includes('://') ? getrimmt : `http://${getrimmt}`).hostname.toLowerCase();
    return host || null;
  } catch {
    return null;
  }
}

const INTERNE_ENDUNGEN = ['.localhost', '.internal', '.intern', '.local'];

/**
 * Spiegelt `KiEndpunktRegion` im Backend: interne Adressen gelten als lokal, alles andere
 * und jede unlesbare Adresse als Drittland — im Zweifel die strengere Region.
 */
export function regionVonEndpunkt(adresse: string | null | undefined): KiRegion {
  const host = hostVon(adresse)?.replace(/\.$/, '');
  if (!host) return 'DRITTLAND';
  const ip = IPV4.exec(host);
  if (!ip) {
    const intern = !host.includes('.') || host === 'localhost' || INTERNE_ENDUNGEN.some((e) => host.endsWith(e));
    return intern ? 'LOKAL' : 'DRITTLAND';
  }
  const [a, b, c, d] = ip.slice(1, 5).map(Number);
  if ([a, b, c, d].some((oktett) => oktett > 255)) return 'DRITTLAND';
  const intern =
    a === 127 ||
    a === 10 ||
    (a === 172 && b >= 16 && b <= 31) ||
    (a === 192 && b === 168) ||
    (a === 100 && b >= 64 && b <= 127) ||
    (a === 192 && b === 0 && c === 0) ||
    (a === 198 && (b === 18 || b === 19));
  return intern ? 'LOKAL' : 'DRITTLAND';
}

/** Ohne Schema gilt wie im Backend http, bei Azure OpenAI https (dort ist nur https erlaubt). */
function schemaVon(anbieter: KiAnbieterTyp | null | undefined, adresse: string): string {
  return adresse.includes('://') ? '' : anbieter === 'AZURE_OPENAI' ? 'https://' : 'http://';
}

/**
 * Gegenstelle aus Schema, Host und effektivem Port wie im Backend: Das gespeicherte Token gilt nur für
 * dieselbe Gegenstelle. Ein ausgeschriebener Standardport (https 443, http 80) ist kein Wechsel.
 */
export function gegenstelleVon(
  anbieter: KiAnbieterTyp | null | undefined,
  adresse: string | null | undefined,
): string | null {
  const getrimmt = (adresse ?? '').trim();
  if (!getrimmt) return null;
  try {
    const url = new URL(schemaVon(anbieter, getrimmt) + getrimmt);
    const standardPort = url.protocol === 'https:' ? '443' : url.protocol === 'http:' ? '80' : '';
    return `${url.protocol}//${url.hostname.toLowerCase()}:${url.port || standardPort}`;
  } catch {
    return null;
  }
}
